import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingDown, TrendingUp, Trophy } from "lucide-react";

/**
 * Phase 4 — P&L Waterfall + Company Scorecard.
 * Waterfall:  Revenue  →  −Variable  =  Contribution  →  −Fixed  =  EBITDA
 * Scorecard:  every outlet's revenue / variable / fixed / EBITDA %, ranked.
 */
export default function PnLDiagnostic() {
  const today = new Date().toISOString().slice(0, 7); // YYYY-MM
  const [me, setMe] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [month, setMonth] = useState(today);
  const [pnl, setPnl] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = me?.role === "admin";

  useEffect(() => {
    Promise.all([
      api.get("/auth/me").then(r => { setMe(r.data); if (r.data.role !== "admin") setOutletId(r.data.outlet_id || ""); }),
      api.get("/ops/outlets").then(r => setOutlets(r.data)),
    ]).catch(() => {});
  }, []);

  useEffect(() => { if (isAdmin ? outletId : me) run(); }, [outletId, month, me]);

  const run = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("month", month);
      if (isAdmin) params.set("outlet_id", outletId);
      const r = await api.get(`/ops/pnl?${params}`);
      setPnl(r.data);
      if (isAdmin) {
        const s = await api.get(`/ops/scorecard?month=${month}`);
        setScorecard(s.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load P&L"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="pnl-diagnostic-root">
      {/* Header controls */}
      <div className="rounded-2xl p-4 border flex flex-wrap gap-3 items-end" style={{ background: "#FFFDF6", borderColor: "#E4D6A8" }}>
        {isAdmin && (
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Outlet</label>
            <select data-testid="pnl-outlet" value={outletId} onChange={ev => setOutletId(ev.target.value)} className="h-9 px-2 rounded border bg-white min-w-[220px]" style={{borderColor:"#E4D6A8"}}>
              <option value="">— pick outlet —</option>
              {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Month</label>
          <input data-testid="pnl-month" type="month" value={month} onChange={ev => setMonth(ev.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
        </div>
        <Button data-testid="pnl-refresh" size="sm" onClick={run} disabled={busy}
          className="h-9 rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722] disabled:opacity-60">
          {busy ? "Loading…" : "Refresh"}
        </Button>
        {pnl && <div className="ml-auto text-xs" style={{color:"#7a6349"}}>{pnl.records.sales} sales · {pnl.records.expenses} expense rows</div>}
      </div>

      {(!pnl || (isAdmin && !outletId)) ? (
        <div className="rounded-2xl border border-dashed p-10 text-center text-sm" style={{borderColor:"#E4D6A8", color:"#7a6349"}}>
          {isAdmin ? "Pick an outlet to see its profit story." : "Loading…"}
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Revenue"      value={pnl.revenue.total}      pct={100}                       tone="pos" testid="kpi-revenue" />
            <Kpi label="Variable exp" value={pnl.variable.total}     pct={pctOf(pnl.variable.total, pnl.revenue.total)} tone="neg" testid="kpi-variable" />
            <Kpi label="Contribution" value={pnl.contribution}       pct={pnl.contribution_pct}      tone="mid" testid="kpi-contribution" />
            <Kpi label="Fixed exp"    value={pnl.fixed.total}        pct={pctOf(pnl.fixed.total, pnl.revenue.total)}    tone="neg" testid="kpi-fixed" />
            <Kpi label="EBITDA"       value={pnl.ebitda}             pct={pnl.ebitda_pct}            tone={pnl.ebitda >= 0 ? "pos" : "neg"} testid="kpi-ebitda" />
          </div>

          {/* Waterfall */}
          <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E4D6A8" }}>
            <h3 className="font-display text-xl mb-1" style={{color:"#2b1a0d"}}>Profit waterfall · {pnl.outlet.name} · {pnl.month}</h3>
            <p className="text-xs mb-4" style={{color:"#7a6349"}}>Each bar shows where the money goes. Read left → right.</p>
            <Waterfall pnl={pnl} />
          </div>

          {/* Revenue mix + Expense breakdown side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E4D6A8" }}>
              <h3 className="font-display text-lg mb-3" style={{color:"#2b1a0d"}}>Revenue mix</h3>
              <MixBar segments={[
                { label: "Aggregator", value: pnl.revenue.aggregator, color: "#B4841F" },
                { label: "Payment",    value: pnl.revenue.payment,    color: "#F5C242" },
                { label: "Other",      value: pnl.revenue.other,      color: "#8a5a1a" },
              ]} />
              <table className="w-full text-sm mt-4">
                <thead className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>
                  <tr><th className="text-left py-1">Channel</th><th className="text-left py-1">Type</th><th className="text-right py-1">₹</th><th className="text-right py-1">Bills</th></tr>
                </thead>
                <tbody>
                  {pnl.revenue.by_channel.map(c => (
                    <tr key={c.name} className="border-t" style={{ borderColor: "#F3E7C4" }}>
                      <td className="py-1.5 pr-2 font-medium">{c.name}</td>
                      <td className="py-1.5 pr-2 text-xs uppercase" style={{color:"#8a5a1a"}}>{c.type}</td>
                      <td className="py-1.5 pr-2 text-right">₹{c.amount.toLocaleString()}</td>
                      <td className="py-1.5 text-right">{c.bills}</td>
                    </tr>
                  ))}
                  {pnl.revenue.by_channel.length === 0 && (
                    <tr><td colSpan={4} className="py-4 text-center text-xs" style={{color:"#7a6349"}}>No sales logged this month</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E4D6A8" }}>
              <h3 className="font-display text-lg mb-3" style={{color:"#2b1a0d"}}>Expenses · biggest first</h3>
              <ExpenseBars title="Variable" rows={pnl.variable.by_category} tone="#c0392b" total={pnl.variable.total} />
              <div className="mt-4">
                <ExpenseBars title="Fixed" rows={pnl.fixed.by_category} tone="#4b2e83" total={pnl.fixed.total} />
              </div>
            </div>
          </div>

          {/* Diagnostic notes */}
          <DiagnosticBanner pnl={pnl} />
        </>
      )}

      {/* Company Scorecard (admin only) */}
      {isAdmin && scorecard && (
        <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E4D6A8" }} data-testid="scorecard-root">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <div className="font-mono2 text-[10px] tracking-[0.4em] uppercase" style={{color:"#B4841F"}}>Company scorecard</div>
              <h3 className="font-display text-2xl" style={{color:"#2b1a0d"}}>All outlets · {scorecard.month}</h3>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase font-mono2 tracking-widest" style={{color:"#7a6349"}}>Company EBITDA</div>
              <b style={{color: scorecard.company.ebitda >= 0 ? "#0a7a3b" : "#c0392b"}}>
                ₹{scorecard.company.ebitda.toLocaleString()} <span className="opacity-70 text-sm">({scorecard.company.ebitda_pct}%)</span>
              </b>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>
                <tr>
                  <th className="text-left py-2">#</th>
                  <th className="text-left py-2">Outlet</th>
                  <th className="text-right py-2">Revenue</th>
                  <th className="text-right py-2">Variable</th>
                  <th className="text-right py-2">Fixed</th>
                  <th className="text-right py-2">EBITDA</th>
                  <th className="text-right py-2">EBITDA %</th>
                </tr>
              </thead>
              <tbody>
                {scorecard.outlets.map(o => (
                  <tr key={o.outlet_id} className="border-t" style={{ borderColor: "#F3E7C4" }} data-testid={`sc-row-${o.outlet_code || o.outlet_id}`}>
                    <td className="py-2 pr-2 font-mono2 text-xs">
                      {o.rank === 1 && o.revenue > 0 ? <Trophy size={14} className="inline mr-1" style={{color:"#B4841F"}} /> : null}
                      {o.rank}
                    </td>
                    <td className="py-2 pr-2 font-medium" style={{color:"#2b1a0d"}}>{o.name}</td>
                    <td className="py-2 pr-2 text-right">₹{o.revenue.toLocaleString()}</td>
                    <td className="py-2 pr-2 text-right" style={{color:"#c0392b"}}>₹{o.variable.toLocaleString()}</td>
                    <td className="py-2 pr-2 text-right" style={{color:"#4b2e83"}}>₹{o.fixed.toLocaleString()}</td>
                    <td className="py-2 pr-2 text-right font-bold" style={{color: o.ebitda >= 0 ? "#0a7a3b" : "#c0392b"}}>₹{o.ebitda.toLocaleString()}</td>
                    <td className="py-2 pl-2 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${o.ebitda_pct >= 15 ? "bg-emerald-100 text-emerald-700" : o.ebitda_pct >= 0 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {o.ebitda_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-bold" style={{borderColor:"#B4841F", background:"#FFF8E7"}}>
                  <td colSpan={2} className="py-2 pr-2">Company total</td>
                  <td className="py-2 pr-2 text-right">₹{scorecard.company.revenue.toLocaleString()}</td>
                  <td className="py-2 pr-2 text-right">₹{scorecard.company.variable.toLocaleString()}</td>
                  <td className="py-2 pr-2 text-right">₹{scorecard.company.fixed.toLocaleString()}</td>
                  <td className="py-2 pr-2 text-right">₹{scorecard.company.ebitda.toLocaleString()}</td>
                  <td className="py-2 pl-2 text-right">{scorecard.company.ebitda_pct}%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function pctOf(a, b) { return b ? Math.round((a / b) * 1000) / 10 : 0; }

// -----------------------------------------------------------------------------
function Kpi({ label, value, pct, tone, testid }) {
  const color = tone === "pos" ? "#0a7a3b" : tone === "neg" ? "#c0392b" : "#5b4530";
  return (
    <div className="rounded-xl p-3 border bg-white" style={{ borderColor: "#E4D6A8" }} data-testid={testid}>
      <div className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>{label}</div>
      <div className="font-display text-2xl mt-0.5" style={{ color }}>₹{Number(value || 0).toLocaleString()}</div>
      <div className="text-xs" style={{ color: "#7a6349" }}>{pct}% of revenue</div>
    </div>
  );
}

function Waterfall({ pnl }) {
  const rev = pnl.revenue.total || 1;
  const bars = [
    { label: "Revenue",      value: pnl.revenue.total,      color: "#0a7a3b" },
    { label: "− Variable",   value: pnl.variable.total,     color: "#c0392b" },
    { label: "= Contribution", value: pnl.contribution,     color: pnl.contribution >= 0 ? "#0a7a3b" : "#c0392b" },
    { label: "− Fixed",      value: pnl.fixed.total,        color: "#4b2e83" },
    { label: "= EBITDA",     value: pnl.ebitda,             color: pnl.ebitda >= 0 ? "#0a7a3b" : "#c0392b" },
  ];
  return (
    <div className="space-y-2">
      {bars.map((b, i) => {
        const pct = Math.min(100, Math.abs(b.value) / rev * 100);
        return (
          <div key={i} className="flex items-center gap-3 text-sm">
            <div className="w-28 shrink-0 font-medium" style={{color:"#2b1a0d"}}>{b.label}</div>
            <div className="flex-1 bg-[#FBF5E4] rounded-full h-6 relative overflow-hidden border" style={{borderColor:"#E4D6A8"}}>
              <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{ width: `${pct}%`, background: b.color }} />
              <div className="absolute inset-0 flex items-center px-3 text-xs font-medium" style={{color:"#2b1a0d"}}>
                ₹{Number(b.value).toLocaleString()} <span className="opacity-60 ml-2">({pctOf(b.value, rev)}%)</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MixBar({ segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <>
      <div className="flex h-4 rounded-full overflow-hidden border" style={{borderColor:"#E4D6A8"}}>
        {segments.map((s, i) => (
          <div key={i} title={`${s.label} · ₹${s.value.toLocaleString()}`}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-2 text-xs" style={{color:"#5b4530"}}>
        {segments.map((s, i) => (
          <span key={i} className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{background:s.color}} />
            {s.label} · ₹{s.value.toLocaleString()} ({pctOf(s.value, total)}%)
          </span>
        ))}
      </div>
    </>
  );
}

function ExpenseBars({ title, rows, tone, total }) {
  const max = Math.max(...rows.map(r => r.amount), 1);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>{title}</div>
        <b style={{color:tone}}>₹{total.toLocaleString()}</b>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs" style={{color:"#7a6349"}}>No {title.toLowerCase()} expenses logged.</div>
      ) : (
        <div className="space-y-1.5">
          {rows.slice(0, 8).map(r => (
            <div key={r.name} className="flex items-center gap-2 text-sm">
              <div className="w-32 shrink-0 truncate" style={{color:"#2b1a0d"}}>{r.name}</div>
              <div className="flex-1 h-3 rounded-full bg-[#FBF5E4] border overflow-hidden" style={{borderColor:"#F3E7C4"}}>
                <div className="h-full rounded-full" style={{ width: `${(r.amount / max) * 100}%`, background: tone, opacity: 0.75 }} />
              </div>
              <div className="w-20 text-right text-xs">₹{r.amount.toLocaleString()}</div>
              <div className="w-12 text-right text-xs" style={{color:"#7a6349"}}>{r.pct_of_revenue}%</div>
            </div>
          ))}
          {rows.length > 8 && <div className="text-[10px] mt-1" style={{color:"#7a6349"}}>+ {rows.length - 8} more</div>}
        </div>
      )}
    </div>
  );
}

function DiagnosticBanner({ pnl }) {
  const notes = [];
  if (pnl.revenue.total === 0) {
    notes.push({ kind: "info", msg: "No sales logged for this month yet. Ask the outlet manager to key EOD sales in the Daily Entry grid." });
  } else {
    if (pnl.variable.total / pnl.revenue.total > 0.55) {
      notes.push({ kind: "warn", msg: `Variable expenses are ${Math.round(pnl.variable.total/pnl.revenue.total*100)}% of revenue — food cost or packaging leaks likely.` });
    }
    if (pnl.fixed.total / pnl.revenue.total > 0.30) {
      notes.push({ kind: "warn", msg: `Fixed costs eat ${Math.round(pnl.fixed.total/pnl.revenue.total*100)}% of revenue — revenue is too low for the current cost base.` });
    }
    if (pnl.ebitda < 0) {
      notes.push({ kind: "bad", msg: `EBITDA is negative (₹${Math.abs(pnl.ebitda).toLocaleString()}) — this outlet is bleeding cash this month.` });
    } else if (pnl.ebitda_pct >= 20) {
      notes.push({ kind: "good", msg: `Healthy ${pnl.ebitda_pct}% EBITDA — this outlet is a benchmark for the group.` });
    }
    const topExp = pnl.variable.by_category[0];
    if (topExp && topExp.pct_of_revenue > 25) {
      notes.push({ kind: "warn", msg: `${topExp.name} alone is ${topExp.pct_of_revenue}% of revenue — worth a vendor audit.` });
    }
  }
  if (notes.length === 0) return null;
  return (
    <div className="rounded-2xl border p-4" style={{ background: "#FFF8E7", borderColor: "#E4D6A8" }} data-testid="pnl-diagnostic-banner">
      <div className="font-mono2 text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: "#B4841F" }}>Diagnostic signals</div>
      <ul className="space-y-1.5 text-sm">
        {notes.map((n, i) => (
          <li key={i} className="flex items-start gap-2" style={{color:"#2b1a0d"}}>
            {n.kind === "good" ? <TrendingUp size={14} style={{color:"#0a7a3b", marginTop:2}} /> :
             n.kind === "bad"  ? <TrendingDown size={14} style={{color:"#c0392b", marginTop:2}} /> :
             <span className="inline-block w-2 h-2 rounded-full mt-1.5" style={{background: n.kind === "warn" ? "#c78200" : "#5b4530"}} />}
            <span>{n.msg}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
